import type { Locale } from '@eneco/cms-types';

import type { TransformFunctions } from './types';

interface TransformableParams {
  site: string;
  locale: Locale;
  item: string;
}

interface TransformedParams {
  transformedSite: string;
  transformedLocale: Locale;
  transformedItem: string;
}

/**
 * Allow a container to remap the `site`, `locale`, and `item` it sends to
 * the CMS based on its routing rules. Unchanged from the legacy
 * `@eneco/sitecore-common.applyContainerSpecificTransformations` so callers
 * can swap import paths in Phase C without behavioural changes.
 */
export const applyContainerSpecificTransformations = (
  params: TransformableParams,
  transformFunctions?: TransformFunctions,
): TransformedParams => {
  const { site, locale, item } = params;
  return {
    transformedSite: transformFunctions?.transformSite?.({ site, locale, item }) ?? site,
    transformedLocale: transformFunctions?.transformLocale?.({ site, locale, item }) ?? locale,
    transformedItem: transformFunctions?.transformItem?.({ site, locale, item }) ?? item,
  };
};

const VALUE_DELIMITER = ':';
const PAIR_DELIMITER = '|';

type Params = Record<string, string | null>;

/**
 * Serialize an object of key→value pairs into Sitecore's personalization
 * scope string format: `|key1:value1|key2|key3:value3|`. Values that are
 * nullish are emitted as keys without a value.
 *
 * Identical to the legacy `@eneco/sitecore-common.serializePersonalizationParams`.
 */
export const serializePersonalizationParams = (params: Params = {}): string => {
  const serialized = Object.entries(params)
    .map(([key, value]) => {
      const serializedValue = value ? `${VALUE_DELIMITER}${value}` : '';
      return `${key}${serializedValue}`;
    })
    .join(PAIR_DELIMITER);
  return `${PAIR_DELIMITER}${serialized}${PAIR_DELIMITER}`;
};
